import { TabList, Tab, makeStyles, tokens } from '@fluentui/react-components';

import { spacing } from '@/theme/tokens';

/**
 * Allowed dashboard window sizes (days). Resource Health's history
 * endpoint retains roughly 30 days, so 30 is the practical ceiling.
 * 1d / 7d give faster signals for incident response (a 30d view dilutes
 * a 2-hour outage to near-100%).
 */
export type DashboardWindowDays = 1 | 7 | 30;

interface DashboardWindowPickerProps {
  value: DashboardWindowDays;
  onChange: (next: DashboardWindowDays) => void;
}

const OPTIONS: { key: DashboardWindowDays; label: string }[] = [
  { key: 1, label: '24 hours' },
  { key: 7, label: '7 days' },
  { key: 30, label: '30 days' },
];

const OPTION_KEYS: readonly DashboardWindowDays[] = OPTIONS.map((r) => r.key);

function isDashboardWindowDays(value: unknown): value is DashboardWindowDays {
  return typeof value === 'number' && (OPTION_KEYS as readonly number[]).includes(value);
}

const useStyles = makeStyles({
  wrapper: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: `${spacing.xxs} ${spacing.xs}`,
    width: 'fit-content',
  },
});

export function DashboardWindowPicker({ value, onChange }: DashboardWindowPickerProps) {
  const classes = useStyles();
  return (
    <div className={classes.wrapper}>
      <TabList
        size="small"
        appearance="subtle"
        selectedValue={value}
        onTabSelect={(_, data) => {
          // Fluent's `data.value` is typed `unknown`; guard before casting
          // so an unexpected tab id no-ops instead of corrupting state.
          if (isDashboardWindowDays(data.value)) {
            onChange(data.value);
          }
        }}
        aria-label="Dashboard time window"
      >
        {OPTIONS.map((r) => (
          <Tab key={r.key} value={r.key}>
            {r.label}
          </Tab>
        ))}
      </TabList>
    </div>
  );
}
